/*
 * /prescription/topical/$patientId — Topical prescription management page
 *
 * Allows practitioners to create topical (compounding pharmacy) prescriptions
 * for a patient. Saves the prescription, links products, and generates a
 * filled PDF in the background.
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Checkbox,
	Container,
	Label,
	Page,
	PageHeader,
	Placeholder,
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Textarea,
	toast,
} from "@emr/react-ui"
import { useAuth } from "@emr/auth/client"
import { createFileRoute } from "@tanstack/react-router"
import { FileText, Plus, X } from "lucide-react"
import { useEffect, useState } from "react"
import { trpc } from "../../../trpc"
import { rolePageLoader } from "../../../utils/auth.utils"

export const Route = createFileRoute("/prescription/topical/$patientId")({
	component: TopicalPrescriptionPage,
	loader: rolePageLoader(["practitioner", "nurse", "admin", "super_admin"]),
})

/* ─── Constants ─────────────────────────────────────────────────────────── */

const GRAMS_OPTIONS = [
	{ value: 50,    label: "50 g" },
	{ value: 75,    label: "75 g" },
	{ value: 100,   label: "100 g" },
	{ value: 200,   label: "200 g" },
	{ value: null,  label: "Other" },
]

const REFILLS_OPTIONS = [
	{ value: 1,  label: "1" },
	{ value: 2,  label: "2" },
	{ value: 4,  label: "4" },
	{ value: 6,  label: "6" },
	{ value: 11, label: "11" },
]

/* ─── Page ──────────────────────────────────────────────────────────────── */

function TopicalPrescriptionPage() {
	const { patientId } = Route.useParams()
	const { user, role } = useAuth()
	const [showForm, setShowForm] = useState(false)

	const isAdmin = role === "admin" || role === "super_admin"

	const { data: patient } = trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	const { data: allPractitioners } = trpc.practitioner.getAllByFilter.useQuery(
		{ where: { active: true } },
		{ enabled: isAdmin },
	)
	const { data: myPractitioner } = trpc.practitioner.getByFilter.useQuery(
		{ where: { userId: user?.id } },
		{ enabled: !isAdmin },
	)

	const { data: topicalProducers } = trpc.producer.getAllByFilter.useQuery({
		where: { active: true },
	})
	const topicalOnlyProducers = (topicalProducers ?? []).filter((p: any) => p.isTopicalProducer)

	const { data: existingPrescriptions = [], refetch: refetchPrescriptions } =
		trpc.topicalPrescription.getAllForPatient.useQuery({ patientId })

	return (
		<Page>
			<Container width="xl" padding="lg" gap="lg">
				<PageHeader title="Topical Prescriptions" />

				{patient && (
					<p className="text-sm text-muted-foreground -mt-4">
						{patient.firstName} {patient.lastName}
					</p>
				)}

				{/* New prescription button */}
				{!showForm && (
					<div className="flex justify-end">
						<Button onClick={() => setShowForm(true)}>
							<Plus className="w-4 h-4 mr-1.5" />
							New Topical Prescription
						</Button>
					</div>
				)}

				{/* New prescription form */}
				{showForm && (
					<TopicalPrescriptionForm
						patientId={patientId}
						practitioners={isAdmin ? (allPractitioners ?? []) : myPractitioner ? [myPractitioner] : []}
						defaultPractitionerId={!isAdmin ? myPractitioner?.id : undefined}
						topicalProducers={topicalOnlyProducers}
						onSuccess={() => {
							setShowForm(false)
							refetchPrescriptions()
							toast.success("Topical prescription saved — PDF generating in background")
						}}
						onCancel={() => setShowForm(false)}
					/>
				)}

				{/* Historic prescriptions */}
				{!showForm && (existingPrescriptions.length === 0 ? (
					<Placeholder title="No topical prescriptions">Create one above.</Placeholder>
				) : (
					<div className="flex flex-col gap-4">
						{existingPrescriptions.map((rx: any) => (
							<HistoricTopicalCard key={rx.id} rx={rx} />
						))}
					</div>
				))}
			</Container>
		</Page>
	)
}

/* ─── Form ──────────────────────────────────────────────────────────────── */

type FormProps = {
	patientId: string
	practitioners: any[]
	defaultPractitionerId?: string
	topicalProducers: any[]
	onSuccess: () => void
	onCancel: () => void
}

function TopicalPrescriptionForm({
	patientId,
	practitioners,
	defaultPractitionerId,
	topicalProducers,
	onSuccess,
	onCancel,
}: FormProps) {
	const [practitionerId, setPractitionerId] = useState(defaultPractitionerId ?? "")
	const [producerId, setProducerId]           = useState("")
	const [selectedProductIds, setSelectedProductIds] = useState<string[]>([])
	// null = "Other"
	const [grams, setGrams]                     = useState<number | null | undefined>(undefined)
	const [otherGrams, setOtherGrams]           = useState("")
	const [refills, setRefills]                 = useState(1)
	const [otherCompounded, setOtherCompounded] = useState("")
	const [notes, setNotes]                     = useState("")

	// Load products for the selected producer
	const { data: producerProducts } = trpc.product.getAllByFilter.useQuery(
		{ where: { producerId, active: true } },
		{ enabled: Boolean(producerId) },
	)
	// Only show products that have a pdfFieldName configured
	const topicalProducts = (producerProducts ?? []).filter((p: any) => p.pdfFieldName)

	// Clear product selection when producer changes
	useEffect(() => {
		setSelectedProductIds([])
	}, [producerId])

	const createFull = trpc.topicalPrescription.createFull.useMutation({
		onSuccess,
		onError: (e) => toast.error(e.message),
	})

	function toggleProduct(id: string) {
		setSelectedProductIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
		)
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault()
		if (!producerId) { toast.error("Please select a topical producer"); return }
		if (selectedProductIds.length === 0) { toast.error("Please select at least one product"); return }
		if (grams === undefined) { toast.error("Please select a grams amount"); return }
		if (grams === null && !otherGrams.trim()) { toast.error("Please enter the grams amount"); return }

		await createFull.mutateAsync({
			patientId,
			practitionerId: practitionerId || null,
			producerId,
			grams:    grams,
			otherGrams: grams === null ? otherGrams.trim() : null,
			refills,
			otherCompoundedPrescription: otherCompounded.trim() || null,
			notes:    notes.trim() || null,
			productIds: selectedProductIds,
		})
	}

	const practitionerOptions = practitioners.map((p: any) => ({
		value: p.id,
		label: [p.firstName, p.lastName].filter(Boolean).join(" "),
	}))
	const producerOptions = topicalProducers.map((p: any) => ({
		value: p.id,
		label: p.name,
	}))

	return (
		<Card>
			<CardHeader>
				<CardTitle>New Topical Prescription</CardTitle>
			</CardHeader>
			<CardContent>
				<form onSubmit={handleSubmit} className="flex flex-col gap-6">

					{/* Practitioner */}
					{practitioners.length > 1 && (
						<div className="flex flex-col gap-1.5">
							<Label>Prescriber</Label>
							<Select value={practitionerId} onValueChange={setPractitionerId}>
								<SelectTrigger>
									<SelectValue placeholder="Select prescriber" />
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										{practitionerOptions.map((o) => (
											<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
										))}
									</SelectGroup>
								</SelectContent>
							</Select>
						</div>
					)}

					{/* Producer */}
					<div className="flex flex-col gap-1.5">
						<Label>Compounding Pharmacy / Producer</Label>
						{topicalProducers.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								No topical producers configured. Mark a producer as "Topical Producer" in the Producers section.
							</p>
						) : (
							<Select value={producerId} onValueChange={setProducerId}>
								<SelectTrigger>
									<SelectValue placeholder="Select producer" />
								</SelectTrigger>
								<SelectContent>
									<SelectGroup>
										{producerOptions.map((o) => (
											<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
										))}
									</SelectGroup>
								</SelectContent>
							</Select>
						)}
					</div>

					{/* Products */}
					{producerId && (
						<div className="flex flex-col gap-2">
							<Label>Products</Label>
							{topicalProducts.length === 0 ? (
								<p className="text-sm text-muted-foreground">
									No products with a PDF field name configured for this producer.
									Set a "PDF Field Name" on each product in the Producers section.
								</p>
							) : (
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
									{topicalProducts.map((p: any) => (
										<label
											key={p.id}
											className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
										>
											<Checkbox
												checked={selectedProductIds.includes(p.id)}
												onCheckedChange={() => toggleProduct(p.id)}
												className="mt-0.5"
											/>
											<div className="min-w-0">
												<p className="text-sm font-medium leading-tight">{p.name}</p>
												{p.pdfFieldName && (
													<p className="text-xs font-mono text-muted-foreground mt-0.5">
														{p.pdfFieldName}
													</p>
												)}
											</div>
										</label>
									))}
								</div>
							)}
						</div>
					)}

					{/* Grams */}
					<div className="flex flex-col gap-2">
						<Label>Grams (MITTE)</Label>
						<div className="flex flex-wrap gap-2">
							{GRAMS_OPTIONS.map((opt) => (
								<Button
									key={String(opt.value)}
									type="button"
									variant={grams === opt.value ? "default" : "outline"}
									size="sm"
									onClick={() => setGrams(opt.value)}
								>
									{opt.label}
								</Button>
							))}
						</div>
						{grams === null && (
							<input
								type="text"
								value={otherGrams}
								onChange={(e) => setOtherGrams(e.target.value)}
								placeholder="Enter grams (e.g. 150)"
								className="h-9 rounded-md border bg-background px-3 text-sm w-40"
							/>
						)}
					</div>

					{/* Refills */}
					<div className="flex flex-col gap-2">
						<Label>Refills</Label>
						<div className="flex flex-wrap gap-2">
							{REFILLS_OPTIONS.map((opt) => (
								<Button
									key={opt.value}
									type="button"
									variant={refills === opt.value ? "default" : "outline"}
									size="sm"
									onClick={() => setRefills(opt.value)}
								>
									{opt.label}
								</Button>
							))}
						</div>
					</div>

					{/* Other Compounded Prescription */}
					<div className="flex flex-col gap-1.5">
						<Label>Other Compounded Prescription (optional)</Label>
						<Textarea
							value={otherCompounded}
							onChange={(e) => setOtherCompounded(e.target.value)}
							placeholder="Additional prescription details..."
							rows={2}
						/>
					</div>

					{/* Notes */}
					<div className="flex flex-col gap-1.5">
						<Label>Notes (optional)</Label>
						<Textarea
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							placeholder="Internal notes..."
							rows={2}
						/>
					</div>

					{/* Actions */}
					<div className="flex gap-2">
						<Button type="submit" disabled={createFull.isPending}>
							{createFull.isPending ? "Saving…" : "Save Prescription"}
						</Button>
						<Button type="button" variant="outline" onClick={onCancel}>
							<X className="w-4 h-4 mr-1" />
							Cancel
						</Button>
					</div>
				</form>
			</CardContent>
		</Card>
	)
}

/* ─── Historic card ─────────────────────────────────────────────────────── */

function HistoricTopicalCard({ rx }: { rx: any }) {
	const gramsLabel = rx.grams != null ? `${rx.grams} g` : rx.otherGrams ? `${rx.otherGrams} g` : "—"
	const refillsLabel = `${rx.refills} refill${rx.refills !== 1 ? "s" : ""}`
	const date = rx.createdAt ? new Date(rx.createdAt).toLocaleDateString("en-CA") : "—"

	return (
		<Card>
			<CardContent className="py-4 flex flex-col gap-3">
				<div className="flex items-start justify-between gap-4">
					<div className="flex items-center gap-2">
						<FileText className="w-4 h-4 text-muted-foreground shrink-0" />
						<div>
							<p className="text-sm font-medium">{date}</p>
							<p className="text-xs text-muted-foreground">
								{gramsLabel} · {refillsLabel}
							</p>
						</div>
					</div>
					{!rx.active && <Badge variant="secondary">Inactive</Badge>}
				</div>

				{rx.products?.length > 0 && (
					<div className="flex flex-wrap gap-1.5">
						{rx.products.map((p: any) => (
							<Badge key={p.id} variant="outline" className="text-xs">
								{p.name}
							</Badge>
						))}
					</div>
				)}

				{rx.otherCompoundedPrescription && (
					<p className="text-xs text-muted-foreground border-t pt-2">
						{rx.otherCompoundedPrescription}
					</p>
				)}
			</CardContent>
		</Card>
	)
}
